/**
 * Service worker registration and updates. `PWA-004`, `PWA-005`, `PWA-040`
 * to `PWA-048`.
 *
 * `registerSW` (vite-plugin-pwa's client helper, `virtual:pwa-register`) is
 * fire-and-forget: it never rejects and never throws synchronously, and its
 * own source (`node_modules/vite-plugin-pwa/dist/client/build/register.js`)
 * routes every internal failure -- `navigator.serviceWorker` absent, the
 * `workbox-window` import failing, `wb.register()` rejecting -- through
 * `onRegisterError` rather than propagating. That is what makes `PWA-005`
 * true by construction rather than by a try/catch this file has to remember
 * to keep: nothing here is awaited by the caller, so nothing here can leave
 * the app half-rendered.
 *
 * `PWA-040`/`registerType: 'prompt'` (`vite.config.ts`) is why the returned
 * function is never called until the player consents: read that same
 * client source and it only sends `SKIP_WAITING` (`sendSkipWaitingMessage`)
 * when called, never on its own, and only reloads once the new worker is
 * actually `controlling` (`PWA-043`) -- this file does not implement either
 * of those; it is what `registerSW` already does for `registerType: 'prompt'`,
 * verified by configuration rather than duplicated in application code (the
 * same split `scripts/check-precache.mjs`'s own `findUnconditionalActivation`
 * proves at the build-artifact level).
 *
 * Lives in `src/platform/`, not `src/shell/` -- a platform capability the
 * shell asks for, not shell behaviour, per CLAUDE.md's import table. The one
 * exception is `levelInProgressSignal` (`@shell/level-in-progress`), which
 * `UpdateNotice.tsx` (shell) reads directly rather than this file re-exporting
 * it -- `PWA-042`'s gate is a shell-screen concern, not a platform one.
 */

import { signal } from '@preact/signals';
import { registerSW } from 'virtual:pwa-register';

/** `PWA-047` names this figure. */
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * `PWA-041`: flips true once a new worker is installed and waiting for
 * consent (`registerSW`'s own `onNeedRefresh`, called from exactly that
 * moment -- see the client source quoted above).
 */
export const updateAvailable = signal(false);

/**
 * `PWA-044`. Module-level, not persisted -- a real reload (including the one
 * `applyUpdate` itself eventually causes) clears it for free, which is
 * "this session" exactly: nothing here has to define or bound a session
 * explicitly, because there is nothing left running once one ends.
 */
export const updateDismissed = signal(false);

export function dismissUpdate(): void {
  updateDismissed.value = true;
}

/**
 * `PWA-047`, factored out from any real timer so it can be tested with a
 * fake clock rather than real elapsed time (`pwa.test.ts`). `checkNow` is
 * `registration.update()` in production -- fetches the worker script byte
 * for byte and fires `onNeedRefresh` again if it has changed. `maybeCheck`'s
 * first call always runs `checkNow` regardless of `intervalMs`
 * (`lastCheckedAt` starts `null`), which is "on start"; every call after
 * that runs it only once `intervalMs` has genuinely elapsed, which is "at
 * most once per hour thereafter" -- the same one function proves both
 * halves of `PWA-047`'s sentence rather than needing two.
 */
export function createUpdateChecker(
  checkNow: () => void,
  intervalMs = CHECK_INTERVAL_MS,
  now: () => number = Date.now,
): { maybeCheck: () => void } {
  let lastCheckedAt: number | null = null;
  function maybeCheck(): void {
    const current = now();
    if (lastCheckedAt !== null && current - lastCheckedAt < intervalMs) return;
    lastCheckedAt = current;
    checkNow();
  }
  return { maybeCheck };
}

let applyUpdateFn: ((reloadPage?: boolean) => Promise<void>) | null = null;
let started = false;

/**
 * `PWA-043`: sends the consent message and lets `registerSW`'s own
 * `controlling` listener reload once the new worker actually takes over --
 * see this file's header for why that reload is not implemented here. Only
 * ever called from `UpdateNotice.tsx`'s "Update now" `onClick` -- nothing in
 * this module calls it from `onNeedRefresh` or anywhere else, which is the
 * whole content of "and does so only on consent"; `pwa.test.ts` proves the
 * negative directly, not just the positive call.
 *
 * `PWA-045`/`PWA-048` (SDD-005, reworded update-flow-coverage loop stage 3)
 * are deliberately not implemented here either, for the same reason neither
 * survived as an independently testable claim: `PWA-042` withholds the
 * update notice -- and with it, the only control that can ever call this
 * function -- entirely while a level is in progress, so by the time an
 * update can be accepted at all, whatever needs to survive the reload is
 * already durably saved through the ordinary backgrounding/leave paths
 * `PROG-034`/`PROG-065` already prove. An update-triggered reload is a real
 * `pagehide` regardless, the same teardown `ADR-0012`'s synchronous relay
 * exists to survive, so even the mechanism is the ordinary one, not a
 * special case this function has to implement.
 */
export function applyUpdate(): void {
  void applyUpdateFn?.();
}

export function registerServiceWorker(): void {
  if (started) return;
  started = true;

  applyUpdateFn = registerSW({
    onNeedRefresh: () => {
      updateAvailable.value = true;
    },
    onRegisterError: (error: unknown) => {
      // The app has already rendered by the time this call is made (see
      // main.tsx) and continues to run from the network -- there is nothing
      // further to do here besides not letting the failure propagate.
      console.warn('Service worker registration failed; continuing without it.', error);
    },
    onRegisteredSW: (_url, registration) => {
      if (!registration) return;
      const { maybeCheck } = createUpdateChecker(() => {
        void registration.update();
      });
      // `maybeCheck`'s own `lastCheckedAt === null` branch is what makes
      // this the "on start" check `PWA-047` names -- called explicitly here
      // rather than left to the registration fetch's own implicit byte
      // comparison, so the requirement's own behaviour is this file's own
      // testable claim rather than an inference about a dependency's
      // internals. The interval covers "at most once per hour thereafter";
      // `maybeCheck`'s own guard is what stops it from ever firing sooner.
      maybeCheck();
      setInterval(maybeCheck, CHECK_INTERVAL_MS);
    },
  });
}
